import { useEffect, useState } from "react";
import useParams from "../../shims/useParams";
import { Header, Message } from "semantic-ui-react";
import Preloader from "../UI/Preloader/Preloader";
import { useRestClientContext } from "../../contexts/RestClientContext";
import { DateTime } from "luxon";

import "./ImportMonitorComponent.scss";

interface FetchImportResponse {
	filename: string;
	created_at: string;
	status: string;
	updated_at: string;
	progress: number;
	log_entries: {
		id: number;
		message: string;
		type: string;
		created_at: string;
	}[];
}

export default function ImportMonitorComponent() {
	const client = useRestClientContext();
	const params = useParams();

	const [loading, setLoading] = useState(true);
	const [data, setData] = useState<FetchImportResponse>();
	const [iteration, setIteration] = useState(0);

	useEffect(() => {
		if (!params.import_id) return;
		if (iteration === 0) setLoading(true);

		client
			.fetchImport(Number(params.import_id))
			.then((response) => response.json())
			.then((data) => {
				setData(data);
				setLoading(false);
			});

		const timeoutId = setTimeout(() => {
			setIteration(iteration + 1);
		}, 5000);

		return () => {
			clearTimeout(timeoutId);
		};
	}, [client, params.import_id, iteration]);

	const formatDateTime = (dateTime: string) => DateTime.fromISO(dateTime).toLocaleString(DateTime.DATETIME_FULL);

	return (
		<>
			<Header as="h1">Import {params.import_id}</Header>
			{loading || data === undefined ? (
				<Preloader />
			) : (
				<>
					<section role="status">
						<p>
							{data.filename} created at {formatDateTime(data.created_at)}
						</p>
						<p>
							<strong>{data.status}</strong> (Last updated at {formatDateTime(data.updated_at)})
						</p>
						<p>
							<progress max="1" value={data.progress} />
						</p>
					</section>
					{data.status && data.status.toLowerCase() === "failed" && (
						<Message negative>
							<Message.Header>Error processing file</Message.Header>
							<p>
								Something went wrong when processing your file. Check the log below for more
								information.
							</p>
						</Message>
					)}
					{data.log_entries.length ? (
						<ol id="import-monitor-log-entries">
							{data.log_entries.map((entry) => {
								const props = {
									[entry.type]: true,
								};

								return (
									<li key={entry.id}>
										<Message {...props}>
											<p>{entry.message}</p>
											<time dateTime={entry.created_at}>{formatDateTime(entry.created_at)}</time>
										</Message>
									</li>
								);
							})}
						</ol>
					) : (
						<Message>No log entries to display</Message>
					)}
				</>
			)}
		</>
	);
}
